import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { differenceInMonths } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/ducks';

import Table from '../index';
import TableSearchOrder from 'components/TableSearchOrder';
import { Visibility } from 'components/Visibility';

import OrderServices from 'services/order.service';

import { IBatchRequestResponse } from 'models/Batch';
import { ordersModelCompleteButton } from './models';
import { IOrdersData, OrdersTabsEnum } from 'models/Order';
import { EnumUserType } from 'models/User';

import {
  formatDateToISO_UTC,
  getMaxDateInSearchFilter,
  getMinDateInSearchFilter,
  validateDatePeriod,
  validateDatePeriodExportData
} from 'utils/Date';
import { DateOutputStr } from 'utils/DateOutput';
import { ValidateLimitDownloadInLot } from 'utils/ValidateLimitdownloadInLot';
import { isMobileDevice } from 'utils/ScreenUtils';
import { restartHubConnection } from 'utils/HubNotification';

import {
  allGroupSituation,
  groupSituationDelivered,
  IOrdersTable,
  OrdersDownloadInLotProps,
  TableFilterStateOrder
} from './interface';

import { useHubNotification } from 'hooks/useHubNotification';

const OrdersTable: React.FC<IOrdersTable> = (props) => {

  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);

  const orderServices = new OrderServices();

  const {
    trackingKey,
    showButton,
    showFilter = false,
    completeBlankSpaces,
    tabFilter,
    tableSearchClassName,
    customProps,
    DropdownTemplate,
    showExportButton,
    showDownloadInLotButton,
    currentTabId,
    clearBtn,

    viewFilters,
    showAdvancedFilters = false,
    showTypeDateFilter = false
  } = props;

  const [tableFilter, setTableFilter] = useState<TableFilterStateOrder>({
    textFilter: "",
    startPeriod: new Date(`${DateOutputStr(getMinDateInSearchFilter()
      .toISOString(), 'yyyy-MM-dd')}`),
    lastPeriod: new Date(`${DateOutputStr(getMaxDateInSearchFilter()
      .toISOString(), 'yyyy-MM-dd')}`),
    filterDate: "BillingDate",
    status: "",
    enterpriseCode: "",
    subsidiary: "",
    orderType: currentTabId?.order || 0
  });
  const [orders, setOrders] = useState<IBatchRequestResponse<IOrdersData>>();
  const [ordersDownloadInLot, setOrdersDownloadInLot] = useState<OrdersDownloadInLotProps>({
    loading: false
  });
  const [tableOrderBy, setTableOrderBy] = useState('orderDate desc, id desc');
  const [isLoadOrder, setIsLoadOrder] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState(0);

  const customer = useSelector((state: RootState) => state.customer);

  const pageTotal = props.pageSize || 10;
  const pageTotalDownloadInLot = 100;
  const isMobile = isMobileDevice();

  useEffect(() => {
    if (!currentTabId) return;
    dispatch({
      type: 'SET_CURRENT_TAB',
      data: currentTabId.name
    });

    dispatch({
      type: 'SET_CURRENT_TAB_PAGE',
      data: currentTabId.order
    });
  }, []);

  useEffect(() => {
    if (shouldGetOrders()) {
      getOrders();
    }
  }, [
    currentPage,
    tableFilter,
    tableOrderBy,
    customer,
    user?.selectedCustomerId,
    user
  ]);

  const shouldGetOrders = () => {
    if (!showFilter) return true;

    if (user?.userType === EnumUserType.CUSTOMER) {
      const selectedCustomerId = user.selectedCustomerId;
      const isValidCustomerId = selectedCustomerId !== undefined && parseInt(selectedCustomerId) > 0;
      return isValidCustomerId && isLoadOrder;
    }

    if (user?.userType === EnumUserType.EMPLOYEE || user?.userType === EnumUserType.SUPPLIER)
      return isLoadOrder;

    return false;
  };

  const getOrders = () => {
    setOrders(undefined);

    const modifiedFilter = {
      ...tableFilter,
      filterDate: currentTabId?.name === OrdersTabsEnum.PEDIDO_PENDENTE ? "orderDate" : tableFilter.filterDate,
    };

    orderServices
      .get(currentPage, pageTotal, tableOrderBy, modifiedFilter)
      .then((result: IBatchRequestResponse<IOrdersData>) => {
        if (result) {
          const { error, data, totalPages, pageSize, total } = result;
          setOrders({ error, data, totalPages, pageSize, total });
        }
      });
  };

  const exportData = useCallback(async () => {

    if (!validateDatePeriodExportData(tableFilter?.startPeriod, tableFilter?.lastPeriod)) return;
    
    await restartHubConnection(useHubNotification());

    await new OrderServices().downloadExportData(
      { ...tabFilter, ...tableFilter }, tableOrderBy
    );
  }, [tabFilter, tableFilter, tableOrderBy]);

  useEffect(() => {
    if (tableFilter && ordersDownloadInLot?.data) {
      setOrdersDownloadInLot({
        ...ordersDownloadInLot,
        data: undefined
      });
    }
  }, [tableFilter]);

  const getOrdersForDownloadInLot = async () => {
    if (ValidateLimitDownloadInLot(orders?.total)) return;
    setOrdersDownloadInLot({ ...ordersDownloadInLot, loading: true });

    await new OrderServices()
      .get(
        0,
        pageTotalDownloadInLot,
        tableOrderBy,
        { ...tabFilter, ...tableFilter }
      )
      .then((result: IBatchRequestResponse<IOrdersData> | undefined) => {
        if (result?.data) {
          setOrdersDownloadInLot({
            ...ordersDownloadInLot,
            data: result?.data,
            loading: false
          });
        }
      });
  };

  const bottomButton = {
    label: '+ Ver todos os pedidos',
    linkTo: '/pedidos',
  };

  const OrdersTableRender = useMemo(
    () => (
      <Table
        trackingKey={trackingKey}
        className="mt-3"
        error={orders?.error}
        onReload={getOrders}
        setTableOrder={setTableOrderBy}
        tableOrder={tableOrderBy}
        currentPage={currentPage}
        data={orders?.data}
        pageSize={pageTotal}
        onPageChange={setCurrentPage}
        DropdownTemplate={DropdownTemplate || undefined}
        totalPages={orders?.totalPages}
        model={props.model || ordersModelCompleteButton()}
        bottomButton={showButton ? bottomButton : undefined}
        completeBlankSpaces={completeBlankSpaces || false}
        customProps={customProps || {}}
        exportButton={
          showExportButton ?
            {
              show: showExportButton,
              onClick: conditionByExportData,
            } :
            undefined
        }
        downloadInLotButton={showDownloadInLotButton}
        getOrdersForDownloadInLot={getOrdersForDownloadInLot}
        ordersDownloadInLot={ordersDownloadInLot}
        totalOrder={orders?.total}
      />
    ),
    [orders, ordersDownloadInLot, tabFilter, user?.selectedCustomerId, customer, tableOrderBy]
  );

  function conditionByExportData() {

    const isValidDate = validateDateBySituation();

    if (!isValidDate) return false;

    return exportData();
  }

  const validateDateBySituation = (): boolean => {
    if (!tableFilter)
      return false;

    const dateFilterStart = tableFilter.startPeriod || getMinDateInSearchFilter();
    const dateFilterEnd = tableFilter.lastPeriod || getMaxDateInSearchFilter();

    const startDate = new Date(dateFilterStart);
    const endDate = new Date(dateFilterEnd);

    const startDateUTC = formatDateToISO_UTC(startDate);
    const endDateUTC = formatDateToISO_UTC(endDate);

    const diffInMonth = differenceInMonths(endDateUTC, startDateUTC);

    if (!tableFilter.status)
      return validateDatePeriod(diffInMonth, false, false);

    const statusArray = tableFilter.status.split(",");
    const ArraysEqualDelivered = JSON.stringify(statusArray) === JSON.stringify(groupSituationDelivered);
    const ArraysEqualAll = JSON.stringify(statusArray) === JSON.stringify(allGroupSituation);

    return validateDatePeriod(diffInMonth, ArraysEqualAll, ArraysEqualDelivered);
  };

  return (
    <>
      <Visibility visible={showFilter}>
        <TableSearchOrder
          placeholder={!isMobile ? "Pedido, nota fiscal, lote, ordem ou produto" : ""}
          setTableFilter={setTableFilter}
          className={tableSearchClassName}
          setCurrentPage={setCurrentPage}
          viewFilters={viewFilters}
          showAdvancedFilters={!isMobile && showAdvancedFilters}
          showTypeDateFilter={showTypeDateFilter}
          setIsLoadOrder={setIsLoadOrder}
          clearBtn={!isMobile && clearBtn}
        />
      </Visibility>

      {OrdersTableRender}
    </>
  );
};

export default OrdersTable;